import type { MouseEvent } from "react";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Card, Flex, List, SimpleGrid, Stack, Text } from "@mantine/core";
import { getSectorHref, HOME_SECTOR_CARDS, type SectorCard } from "./sectorCatalog";
import { defaultSectorVisual, getSectorIcon, sectorVisualBySlug } from "./sectorCardVisual.util";

const SECTOR_CARD_BORDER = "#E2E8F0";
const SECTOR_CARD_BORDER_HOVER = "#CBD5E1";
const SECTOR_CARD_TEXT = "#04305c";

const COMING_SOON_TEXT = "#FFFFFF";
const COMING_SOON_SURFACE = "rgba(2, 6, 23, 0.55)";
const COMING_SOON_CARD_BORDER = "rgba(148, 163, 184, 0.35)";
const COMING_SOON_STARFIELD = "url(/aerospace-space-technology-bg.svg)";

/** Titles at or above this length typically wrap to two lines in the 4-column grid. */
const MULTILINE_TITLE_MIN_LENGTH = 32;

function isMultilineSectorTitle(title: string) {
  return title.length >= MULTILINE_TITLE_MIN_LENGTH;
}

function sortSectorsMultilineFirst(cards: SectorCard[]) {
  return [...cards].sort((a, b) => {
    const aMulti = isMultilineSectorTitle(a.title);
    const bMulti = isMultilineSectorTitle(b.title);
    if (aMulti === bMulti) return 0;
    return aMulti ? -1 : 1;
  });
}

export type SectorCardsGridProps = {
  /** Full grid on home/discover vs single column for discover sidebar filter. */
  variant?: "grid" | "sidebar";
  /** Called when user picks a sector (before navigation) — e.g. Discover moves cards to a left filter column. */
  onFilterEngaged?: () => void;
  /** When provided, sector/list clicks call this instead of navigating to `/sector/:slug`. */
  onSectorSelect?: (sector: SectorCard) => void;
};

function navigateToSectorEcosystem(navigate: ReturnType<typeof useNavigate>, sector: SectorCard) {
  navigate(getSectorHref(sector.slug));
}

export function SectorCardsGrid({
  variant = "grid",
  onFilterEngaged,
  onSectorSelect,
}: SectorCardsGridProps) {
  const navigate = useNavigate();
  const sectorCards = useMemo(
    () => (variant === "grid" ? sortSectorsMultilineFirst(HOME_SECTOR_CARDS) : HOME_SECTOR_CARDS),
    [variant],
  );

  const engage = () => {
    onFilterEngaged?.();
  };

  const stopCardClick = (e: MouseEvent) => {
    e.stopPropagation();
  };

  const handleSectorSelect = (sector: SectorCard) => {
    if (sector.comingSoon) return;
    engage();
    if (onSectorSelect) {
      onSectorSelect(sector);
      return;
    }
    navigateToSectorEcosystem(navigate, sector);
  };

  const gridCols = variant === "sidebar" ? 1 : { base: 1, sm: 2, md: 3, lg: 6 };
  const cardPadding = variant === "sidebar" ? "sm" : "xs";
  const titleSize = variant === "sidebar" ? 13 : 14;
  const titleWeight = 500;
  const iconSize = 14;
  const iconBoxSize = 28;
  const isGrid = variant === "grid";

  return (
    <SimpleGrid
      cols={gridCols}
      spacing={variant === "sidebar" ? "sm" : "xs"}
      verticalSpacing={variant === "sidebar" ? "sm" : "xs"}
    >
      {sectorCards.map((sector) => {
        const SectorIcon = getSectorIcon(sector.slug);
        const isComingSoon = Boolean(sector.comingSoon);
        const visual = sectorVisualBySlug[sector.slug] ?? defaultSectorVisual;
        return (
          <Card
            key={sector.slug}
            withBorder
            radius="md"
            shadow={isComingSoon ? "none" : "xs"}
            p={cardPadding}
            style={{
              cursor: isComingSoon ? "default" : "pointer",
              transition: "box-shadow 150ms ease, border-color 150ms ease, transform 150ms ease",
              ...(variant === "grid" && isComingSoon ? { gridColumn: "span 2" } : {}),
              ...(isComingSoon
                ? {
                    position: "relative",
                    overflow: "hidden",
                    backgroundColor: "#020617",
                    borderColor: COMING_SOON_CARD_BORDER,
                  }
                : {
                    backgroundColor: "#FFFFFF",
                    borderColor: SECTOR_CARD_BORDER,
                  }),
            }}
            styles={
              isComingSoon
                ? undefined
                : {
                    root: {
                      "&:hover": {
                        boxShadow: "var(--mantine-shadow-sm)",
                        borderColor: SECTOR_CARD_BORDER_HOVER,
                        transform: "translateY(-1px)",
                      },
                    },
                  }
            }
            onClick={isComingSoon ? undefined : () => handleSectorSelect(sector)}
          >
            {isComingSoon ? (
              <Box
                aria-hidden
                style={{
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "#020617",
                  backgroundImage: COMING_SOON_STARFIELD,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                  pointerEvents: "none",
                  zIndex: 0,
                }}
              />
            ) : null}
            {(() => {
              const iconBox = (
                <Box
                  style={{
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: iconBoxSize,
                    height: iconBoxSize,
                    borderRadius: 8,
                    backgroundColor: isComingSoon ? COMING_SOON_SURFACE : visual.iconBg,
                  }}
                >
                  <SectorIcon
                    size={iconSize}
                    color={isComingSoon ? COMING_SOON_TEXT : visual.iconColor}
                  />
                </Box>
              );

              const titleText = (
                <Text
                  fz={titleSize}
                  fw={titleWeight}
                  c={isComingSoon ? COMING_SOON_TEXT : SECTOR_CARD_TEXT}
                  lh={1.3}
                  style={{
                    flex: 1,
                    minWidth: 0,
                    wordBreak: "break-word",
                    ...(isComingSoon ? { textShadow: "0 1px 2px rgba(0, 0, 0, 0.45)" } : {}),
                  }}
                >
                  {sector.title}
                </Text>
              );

              const subcategoryList =
                !isComingSoon && sector.subcategories.length > 0 ? (
                  <List size={variant === "sidebar" ? "xs" : "sm"} spacing={6} w="100%">
                    {sector.subcategories.map((item) => (
                      <List.Item
                        key={item}
                        style={{ cursor: "pointer", color: "#04305c", textDecoration: "underline" }}
                        onClick={(e) => {
                          stopCardClick(e);
                          handleSectorSelect(sector);
                        }}
                      >
                        {item}
                      </List.Item>
                    ))}
                  </List>
                ) : null;

              return (
                <Stack
                  gap="xs"
                  h="100%"
                  justify={isGrid ? "center" : undefined}
                  style={isComingSoon ? { position: "relative", zIndex: 1 } : undefined}
                >
                  <Flex align="flex-start" gap={8}>
                    {iconBox}
                    {isComingSoon ? (
                      <Flex
                        align="center"
                        justify="space-between"
                        gap="xs"
                        wrap="nowrap"
                        style={{ flex: 1, minWidth: 0 }}
                      >
                        {titleText}
                        <Text
                          size="xs"
                          fw={500}
                          c={COMING_SOON_TEXT}
                          px={10}
                          py={4}
                          style={{
                            flexShrink: 0,
                            borderRadius: 999,
                            backgroundColor: COMING_SOON_SURFACE,
                            border: "1px solid rgba(255, 255, 255, 0.35)",
                          }}
                        >
                          Coming Soon
                        </Text>
                      </Flex>
                    ) : (
                      titleText
                    )}
                  </Flex>
                  {subcategoryList}
                </Stack>
              );
            })()}
          </Card>
        );
      })}
    </SimpleGrid>
  );
}
